import { v4 as uuidv4 } from 'uuid';
import { toISODateString } from '../utils/date-utils';
/**
 * What sharing should be set to when an account is registered.
 *
 * A NEW account shares its busy times across devices: a calendar that only
 * works on the machine you authorised it on is most of the way to useless,
 * and requiring a sign-in per device is exactly the friction this removes.
 * Titles are not included — those stay on this device unless asked for.
 *
 * An account that already exists keeps whatever it was set to. Flipping an
 * existing connection on would start uploading someone's calendar to their
 * cloud storage without them asking, which is the one thing this subsystem
 * must never do — a default may only apply to a decision not yet made.
 */
export function defaultAccountSharing(existing) {
    return {
        share_across_devices: existing?.share_across_devices ?? true,
        share_privacy: existing?.share_privacy ?? 'busy',
    };
}
/** Rank for a calendar the user hasn't explicitly ordered. */
export const DEFAULT_CALENDAR_PRIORITY = 100;
export function calendarPriority(source) {
    return source?.priority ?? DEFAULT_CALENDAR_PRIORITY;
}
export function createCalendarSource(data) {
    const isPrimary = data.is_primary ?? false;
    return {
        id: uuidv4(),
        account_id: data.account_id,
        calendar_id: data.calendar_id,
        name: data.name,
        color: data.color ?? null,
        is_primary: isPrimary,
        // Only the primary calendar is on by default. Turning on somebody's
        // twelve subscribed calendars unasked would bury their day.
        enabled: isPrimary,
        privacy: 'full',
        blocks_time: true,
        include_declined: false,
        include_all_day: false,
        sync_token: null,
        last_synced_at: null,
    };
}
/** Strip an event down to what the source's privacy setting permits. */
export function applyPrivacy(event, privacy) {
    if (privacy === 'full')
        return event;
    return { ...event, title: null, location: null, organizer: null, html_link: null };
}
/** Does this source want to keep this event at all? */
export function sourceAcceptsEvent(source, event) {
    if (event.all_day && !source.include_all_day)
        return false;
    if (event.response === 'declined' && !source.include_declined)
        return false;
    return true;
}
/**
 * Local-day keys an event touches, so the cache can be queried by day.
 * A meeting that runs past midnight belongs to both days.
 */
export function eventDateKeys(startISO, endISO, allDay) {
    if (allDay) {
        // Google's all-day `end` is exclusive; walk [start, end).
        const keys = [];
        const cursor = new Date(`${startISO.slice(0, 10)}T00:00:00`);
        const stop = new Date(`${endISO.slice(0, 10)}T00:00:00`);
        // Guard against a malformed range producing an unbounded loop.
        let guard = 0;
        while (cursor < stop && guard++ < 400) {
            keys.push(toISODateString(cursor));
            cursor.setDate(cursor.getDate() + 1);
        }
        return keys.length > 0 ? keys : [startISO.slice(0, 10)];
    }
    const start = new Date(startISO);
    const end = new Date(endISO);
    const keys = [];
    const cursor = new Date(start.getFullYear(), start.getMonth(), start.getDate());
    let guard = 0;
    while (cursor <= end && guard++ < 400) {
        keys.push(toISODateString(cursor));
        cursor.setDate(cursor.getDate() + 1);
        if (cursor.getTime() >= end.getTime() && end.getHours() === 0 && end.getMinutes() === 0)
            break;
    }
    return keys.length > 0 ? keys : [toISODateString(start)];
}
function clockTime(d) {
    return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}
/**
 * Turn a day's events into busy slots for the scheduler.
 *
 * Labels are the CALENDAR's name, never the event's title — these end up in
 * `Schedule.constraints.blocked_slots`, which IS synced to the user's cloud
 * storage. A work meeting can shape your day without its subject line being
 * written into your personal Drive.
 *
 * All-day events never block: a birthday shouldn't erase a working day. An
 * event that spans the day boundary is clamped to the day being scheduled.
 */
export function eventsToBusySlots(events, sourcesById, dateStr) {
    const dayStart = new Date(`${dateStr}T00:00:00`);
    const dayEnd = new Date(dayStart.getTime() + 24 * 60 * 60 * 1000);
    // Rank rides alongside rather than on the slot: BusySlot is stored in
    // Schedule.constraints.blocked_slots, which IS synced, and a UI ordering
    // hint has no business being written into the user's cloud storage.
    const ranked = [];
    for (const event of events) {
        if (event.all_day)
            continue;
        const source = sourcesById.get(event.source_id);
        if (!source || !source.enabled || !source.blocks_time)
            continue;
        const start = new Date(event.start);
        const end = new Date(event.end);
        if (!(start < dayEnd) || !(end > dayStart))
            continue;
        const clampedStart = start < dayStart ? dayStart : start;
        const clampedEnd = end > dayEnd ? dayEnd : end;
        if (clampedEnd.getTime() - clampedStart.getTime() < 60 * 1000)
            continue;
        const startTime = clockTime(clampedStart);
        // Midnight-clamped ends read as "00:00", which would sort before the
        // start; the scheduler works in HH:MM within one day, so cap at 23:59.
        const endTime = clampedEnd.getTime() >= dayEnd.getTime() ? '23:59' : clockTime(clampedEnd);
        if (endTime <= startTime)
            continue;
        ranked.push({
            slot: { start: startTime, end: endTime, label: source.name },
            priority: calendarPriority(source),
        });
    }
    // Sort by start, then by the user's calendar preference, so that when two
    // calendars cover the same range the merged label leads with the one they
    // said they care about ("Work + Personal", not "Personal + Work").
    ranked.sort((a, b) => a.slot.start === b.slot.start
        ? a.priority - b.priority
        : a.slot.start < b.slot.start
            ? -1
            : 1);
    return mergeBusySlots(ranked.map((r) => r.slot));
}
/**
 * Group events that are booked over each other, so a day with two calendars
 * can show "these three things are all at 10am" instead of three unrelated
 * rows the reader has to compare by eye.
 *
 * Two rules, both deliberate:
 * - **Nothing is dropped.** Every event that goes in comes out, in exactly one
 *   group. Precedence changes the ORDER inside a group, never its membership —
 *   see `CalendarSource.priority`.
 * - **Back-to-back is not a clash.** 10:00–11:00 and 11:00–12:00 touch but
 *   don't overlap, the same half-open comparison `busyConflicts` uses.
 *
 * All-day events get their own single-event groups: a birthday isn't
 * "happening at the same time" as your 10am in any sense the reader cares
 * about, and it doesn't block time either.
 *
 * Groups come back in start order; within a group, by priority then start.
 */
export function groupOverlappingEvents(events, sourcesById) {
    const priorityOf = (event) => calendarPriority(sourcesById?.get(event.source_id));
    const startOf = (event) => new Date(event.start).getTime();
    const endOf = (event) => new Date(event.end).getTime();
    const allDay = events.filter((e) => e.all_day);
    const timed = events
        .filter((e) => !e.all_day)
        .sort((a, b) => startOf(a) - startOf(b) || priorityOf(a) - priorityOf(b));
    const groups = [];
    let current = [];
    let groupEnd = -Infinity;
    for (const event of timed) {
        if (current.length > 0 && startOf(event) < groupEnd) {
            current.push(event);
            groupEnd = Math.max(groupEnd, endOf(event));
            continue;
        }
        if (current.length > 0)
            groups.push(current);
        current = [event];
        groupEnd = endOf(event);
    }
    if (current.length > 0)
        groups.push(current);
    for (const group of groups) {
        group.sort((a, b) => priorityOf(a) - priorityOf(b) || startOf(a) - startOf(b));
    }
    // All-day events lead the day, as they do in every calendar app.
    return [...allDay.map((e) => [e]), ...groups];
}
/**
 * True when the user has actually expressed a preference between the calendars
 * in this group. Without one, every calendar ranks the same and the group is
 * simply "these happen at once" — no calendar should be visually demoted on the
 * strength of a default.
 */
export function groupHasPreference(group, sourcesById) {
    if (group.length < 2)
        return false;
    const ranks = group.map((e) => calendarPriority(sourcesById?.get(e.source_id)));
    return ranks.some((r) => r !== ranks[0]);
}
/**
 * Which busy ranges a fixed item of ours clashes with.
 *
 * Meetings don't displace habits or hard-pinned tasks — the scheduler leaves
 * both in place (see scheduler.ts) — so the overlap has to be *shown* rather
 * than resolved behind the user's back. Returns the clashing calendar names,
 * deduplicated, or an empty array when the slot is clear.
 */
export function busyConflicts(startTime, endTime, busySlots) {
    const labels = [];
    for (const slot of busySlots) {
        // Half-open comparison: back-to-back items (10:00–11:00, 11:00–12:00)
        // touch but don't clash.
        if (startTime < slot.end && endTime > slot.start && !labels.includes(slot.label)) {
            labels.push(slot.label);
        }
    }
    return labels;
}
/**
 * Collapse overlapping busy ranges. Two calendars showing the same meeting
 * (or back-to-back invites) must not each carve out the scheduler's day.
 */
export function mergeBusySlots(slots) {
    if (slots.length <= 1)
        return slots;
    const sorted = [...slots].sort((a, b) => (a.start < b.start ? -1 : a.start > b.start ? 1 : 0));
    const merged = [sorted[0]];
    for (const slot of sorted.slice(1)) {
        const last = merged[merged.length - 1];
        if (slot.start <= last.end) {
            if (slot.end > last.end)
                last.end = slot.end;
            if (!last.label.includes(slot.label))
                last.label = `${last.label} + ${slot.label}`;
        }
        else {
            merged.push({ ...slot });
        }
    }
    return merged;
}
//# sourceMappingURL=calendar.js.map